import {
  QueriedData,
  showErrorNotification,
  showSuccessNotification,
} from '../assets/useAssets';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useIntl } from 'react-intl';
import { Center, CentersManyResponse } from 'src/api/booking/data-contracts';
import {
  Customer,
  CustomerWriteModel,
  createCustomer,
  deleteCustomer,
  getAllCustomers,
  updateCustomer,
} from 'src/api/customers';

export interface UseCustomersResult {
  customersQuery: QueriedData<Customer[]>;
  createCustomer: (customer: CustomerWriteModel) => void;
  updateCustomer: (data: { id: string; customer: CustomerWriteModel }) => void;
  archiveCustomer: (customerID: string) => void;
}

function useCreateCustomer() {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const { mutate: create } = useMutation(
    (customer: CustomerWriteModel) => createCustomer(customer),
    {
      onSuccess: () => {
        showSuccessNotification('final-customer-create-success-message', intl);
        queryClient.invalidateQueries(['final-customers']);
        queryClient.invalidateQueries(['final-customers', 'centers']);
      },
      onError: () => {
        showErrorNotification('final-customer-create-fail-message', intl);
      },
    },
  );
  return create;
}

export function useCustomers(): UseCustomersResult {
  const query = useQuery({
    queryFn: async () => getAllCustomers(),
    queryKey: ['final-customers'],
  });

  const create = useCreateCustomer();
  const queryClient = useQueryClient();
  const intl = useIntl();
  const { mutate: archive } = useMutation(
    (customerID: string) => deleteCustomer(customerID),
    {
      onSuccess: () => {
        showSuccessNotification('final-customer-archive-success-message', intl);
        queryClient.invalidateQueries(['final-customers']);
        queryClient.invalidateQueries(['final-customers', 'centers']);
      },
      onError: () => {
        showErrorNotification('final-customer-archive-fail-message', intl);
      },
    },
  );
  const updateCustomer = useUpdateCustomer();
  return {
    customersQuery: query,
    createCustomer: create,
    archiveCustomer: archive,
    updateCustomer,
  };
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  const intl = useIntl();

  const { mutate: update } = useMutation(
    ({ id, customer }: { id: string; customer: CustomerWriteModel }) =>
      updateCustomer(id, customer),
    {
      onSuccess: (_, { id }) => {
        showSuccessNotification('final-customer-update-success-message', intl);
        queryClient.invalidateQueries(['final-customers', id]);
        queryClient.invalidateQueries(['final-customers']);
        queryClient.invalidateQueries(['final-customers', 'centers']);
      },
      onError: () => {
        showErrorNotification('final-customer-update-fail-message', intl);
      },
    },
  );
  return update;
}
export type CustomerAggregated = Customer & {
  center: Center;
};
export function useCustomersAggregated(
  customersQuery: QueriedData<Customer[]>,
  centersQuery: QueriedData<CentersManyResponse>,
) {
  const queryKey = ['final-customers', 'centers'];

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<CustomerAggregated[]> => {
      if (!customersQuery.data || !centersQuery.data) {
        return [];
      }

      const centerMap: Record<string, Center> = {};
      centersQuery.data.centers.forEach(
        (center) => (centerMap[center.id] = center),
      );
      return customersQuery.data.map((customer) => ({
        ...customer,
        center: centerMap[customer.centerID],
      }));
    },
    enabled: !!customersQuery.data && !!centersQuery.data,
  });

  return query;
}
